import ReactDataGrid from "react-data-grid";
import { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { Localization, AppMessagesContext, SessionContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { GlassFoldUdBox, TooltipWrapper, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { GlassButton } from "src/components/GlassButtons";
import { ANNManager } from "./ANNManager";
import { AnnotationType, AnnotationContextType, type ANNAnnotationRecord, AnnotationQuality } from "./ANNTypes";
import { ANNCreateStedfaestelse } from "./ANNCreateStedfaestelse";
import { ANNCreateNote } from "./ANNCreateNote";
import { ANNCreateDocument } from "./ANNCreateDocument";
import { blobToDataUrl, downloadOverlayImage, extractS3KeyFromUrl } from "./ANNImageOverlayStorage";
import { MapitStateContext, actionAddDataLayer } from "src/states/MapitState";
import { LayerFunc } from "src/managers/LayerFunc";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { ANNAnnotationActionsCell, ANNAnnotationPrimaryActionCell } from "./ANNAnnotationActionsCell";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, minimizeWindow, WindowId } from "src/WindowManager/WindowState";
import { ANNEditorActionType, useANNEditorContext } from "./ANNEditorState";
import { featureCollection, feature } from "@turf/turf";
import type { Geometry } from "geojson";
import { createANNLLMAnalyzer, type LLMAnalysisResult } from "./ANNLLMAnalyzer";
import { Spinner } from "react-bootstrap";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
interface ServitutAnnotationsTableProps {
   documentId?: string;
   datoloebenr?: string;
   servitut?: {
      dokumentFilnavn?: string;
      aktId?: string;
      aktAliasId?: string;
      tekster?: string[];
      tekstSummarisk?: string;
   };
   stamOplysninger?: {
      indskannetAktDokumentId?: string;
      adresse?: {
         streetName?: string;
         buildingIdentifier?: string;
         postCode?: string;
         districtName?: string;
      };
   };
   xmlDoc?: Document | null;
   viamapTingBog?: any;
   propertyData?: any;
}
interface EjendomAnnotationsTableProps {
   bfeNumber?: string | number;
   group?: string;
}
interface AnnotationsTableProps {
   contextType: AnnotationContextType;
   identifier?: string | number;
   group?: string;
   datoloebenr?: string;
   servitut?: ServitutAnnotationsTableProps['servitut'];
   stamOplysninger?: ServitutAnnotationsTableProps['stamOplysninger'];
   xmlDoc?: Document | null;
   viamapTingBog?: ServitutAnnotationsTableProps['viamapTingBog'];
   propertyData?: ServitutAnnotationsTableProps['propertyData'];
}
export function ServitutAnnotationsTable({ documentId, datoloebenr, servitut, stamOplysninger, xmlDoc, viamapTingBog, propertyData }: ServitutAnnotationsTableProps) {
   return <AnnotationsTable
      contextType={AnnotationContextType.Servitut}
      identifier={documentId}
      datoloebenr={datoloebenr}
      servitut={servitut}
      stamOplysninger={stamOplysninger}
      xmlDoc={xmlDoc}
      viamapTingBog={viamapTingBog}
      propertyData={propertyData}
   />;
}
export function EjendomAnnotationsTable({ bfeNumber, group }: EjendomAnnotationsTableProps) {
   return <AnnotationsTable contextType={AnnotationContextType.Ejendom} identifier={bfeNumber} group={group} />;
}

function isServitutDigital(datoloebenr?: string): boolean {
   if (!datoloebenr) return false;
   const yearMatch = datoloebenr.match(/^(\d{2})(\d{2})(\d{4})-/);
   if (yearMatch) {
      const year = parseInt(yearMatch[3], 10);
      return year > 2009;
   }
   return false;
}

function AnnotationsTable({ contextType, identifier, group, datoloebenr, servitut, stamOplysninger, xmlDoc, viamapTingBog, propertyData }: AnnotationsTableProps) {
   const { dispatch: mapitStateDispatch, state: mapitState } = useContext(MapitStateContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: editorState, dispatch: editorDispatch } = useANNEditorContext();
   const { hasAccessToFeature } = useContext(ApplicationStateContext);

   if (!hasAccessToFeature(Feature.AnnotationModule)) {
      return null;
   }
   const [importingId, setImportingId] = useState<string | null>(null);
   const [showCreateModal, setShowCreateModal] = useState(false);
   const [createType, setCreateType] = useState<AnnotationType | null>(null);
   const [downloadingId, setDownloadingId] = useState<string | null>(null);
   const [deletingId, setDeletingId] = useState<string | null>(null);
   const [editingId, setEditingId] = useState<string | null>(null);
   const [editNoteModalOpen, setEditNoteModalOpen] = useState(false);
   const [annotationToEdit, setAnnotationToEdit] = useState<ANNAnnotationRecord | null>(null);
   const [editDocumentModalOpen, setEditDocumentModalOpen] = useState(false);
   const [documentToEdit, setDocumentToEdit] = useState<ANNAnnotationRecord | null>(null);
   const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(new Set());
   const canVerifyQuality = hasAccessToFeature(Feature.AnnotationAuthUser);
   const canDelete = hasAccessToFeature(Feature.AnnotationDelete);
   const canWrite = hasAccessToFeature(Feature.AnnotationWrite);
   const canUseServitutAIAnalyse = hasAccessToFeature(Feature.ServitutAIAnalyse);
   const [annotations, setAnnotations] = useState<ANNAnnotationRecord[]>([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | undefined>(undefined);
   const [analyzing, setAnalyzing] = useState(false);
   const [analyzingProgress, setAnalyzingProgress] = useState<string>("");
   const organizationId = sessionState?.customerRef;
   const timeoutRef = useRef<NodeJS.Timeout | null>(null);
   const loadAnnotations = useCallback(async (): Promise<ANNAnnotationRecord[]> => {
      if (identifier === undefined || identifier === null || identifier === "") {
         setAnnotations([]);
         setLoading(false);
         setError(undefined);
         return [];
      }
      setLoading(true);
      setError(undefined);
      try {
         const items = await ANNManager.listAnnotations(contextType, identifier, organizationId);
         setAnnotations(items);
         setLoading(false);
         return items;
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         setError(errorMessage);
         setLoading(false);
         return [];
      }
   }, [contextType, identifier, organizationId]);

   useEffect(() => {
      if (timeoutRef.current) {
         clearTimeout(timeoutRef.current);
         timeoutRef.current = null;
      }
      let cancelled = false;
      timeoutRef.current = setTimeout(() => {
         if (!cancelled) {
            loadAnnotations();
         }
      }, 1000);
      return () => {
         cancelled = true;
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
         }
      };
   }, [loadAnnotations]);
   useEffect(() => {
      if (editorState.lastSaveTimestamp) {
         const timeoutId = setTimeout(() => {
            loadAnnotations();
         }, 1000);
         return () => clearTimeout(timeoutId);
      }
   }, [editorState.lastSaveTimestamp, loadAnnotations]);
   const handleOpenEditor = useCallback(() => {
      if (identifier === undefined || identifier === null || identifier === "") {
         return;
      }
      windowDispatch(minimizeWindow(WindowId.PropertyInformationDialog));
      windowDispatch(minimizeWindow(WindowId.ServitutDetails));
      editorDispatch({
         type: ANNEditorActionType.Open,
         payload: { contextType, identifier },
      });
      windowDispatch(openWindow(WindowId.ANNEditor, { contextType, identifier, datoloebenr }));
   }, [contextType, identifier, datoloebenr, windowDispatch, editorDispatch]);
   const handleDelete = useCallback((record: ANNAnnotationRecord) => {
      if (!organizationId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
         return;
      }
      windowDispatch(openWindow(WindowId.ConfirmationDialog, {
         header: "Delete annotation",
         children: Localization.getText("Are you sure you want to delete this annotation? This action cannot be undone."),
         onSubmit: async () => {
            setDeletingId(record.id);
            try {
               await ANNManager.deleteAnnotation(record.id, organizationId);
               await new Promise(resolve => setTimeout(resolve, 1000));
               await loadAnnotations();
            } catch (err: any) {
               appMessageDispatch(actionSetErrorMessage(err?.message || String(err) || Localization.getText("Failed to delete annotation")));
            } finally {
               setDeletingId(null);
            }
         }
      }));
   }, [organizationId, appMessageDispatch, loadAnnotations, windowDispatch]);

   const handleEditNote = useCallback((record: ANNAnnotationRecord) => {
      if (record.type !== AnnotationType.Note) return;
      setAnnotationToEdit(record);
      setEditNoteModalOpen(true);
   }, []);

   const handleEditDocument = useCallback((record: ANNAnnotationRecord) => {
      if (record.type !== AnnotationType.Document) return;
      setDocumentToEdit(record);
      setEditDocumentModalOpen(true);
   }, []);

   const getMeaningfulHistoryEntries = useCallback((record: ANNAnnotationRecord) => {
      const touchedAt = record.lastEditedAt || record.createdAt;
      const entries = [...(record.history || [])].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      if (entries.length > 0 && touchedAt && entries[0].timestamp === touchedAt) {
         return entries.slice(1);
      }
      return entries;
   }, []);

   const getCurrentHistoryComment = useCallback((record: ANNAnnotationRecord) => {
      const entries = [...(record.history || [])].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      return entries[0]?.comment || "";
   }, []);

   const handleEditStedfaestelse = useCallback(async (record: ANNAnnotationRecord) => {
      if (record.type !== AnnotationType.Stedfaestelse || !organizationId) {
         return;
      }
      setEditingId(record.id);
      try {
         const initialLayerIds = new Set(Object.keys(mapitState.layers || {}).map(id => parseInt(id)));

         if (!identifier) {
            appMessageDispatch(actionSetErrorMessage(Localization.getText("No identifier available") || "No identifier available"));
            return;
         }

         windowDispatch(minimizeWindow(WindowId.PropertyInformationDialog));
         windowDispatch(minimizeWindow(WindowId.ServitutDetails));
         windowDispatch(minimizeWindow(WindowId.ANNUnverifiedAnnotations));

         editorDispatch({
            type: ANNEditorActionType.Open,
            payload: { contextType, identifier },
         });

         const layers = record.layers || [];
         const layersToImport = layers.filter(
            (layer) => {
               if (layer.geoJson?.features?.length) {
                  return true;
               }
               if (layer.type === LayerType.ImageOverlay && layer.imageOverlay?.coordinates?.length) {
                  return true;
               }
               return false;
            }
         );

         const geometriesForZoom: Geometry[] = [];

         for (const layerSnapshot of layersToImport) {
            if (layerSnapshot.geoJson?.features?.length) {
               const isImageOverlay = layerSnapshot.type === LayerType.ImageOverlay;
               if (isImageOverlay) {
                  const firstFeature = layerSnapshot.geoJson.features[0];
                  const imgUrl = firstFeature?.properties?._imgUrl;
                  if (!imgUrl) {
                     continue;
                  }
                  const geometry = firstFeature?.geometry;
                  if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) {
                     continue;
                  }
                  geometriesForZoom.push(geometry);
                  const coordinates = geometry.type === "Polygon"
                     ? geometry.coordinates[0]
                     : geometry.coordinates[0]?.[0];
                  if (!coordinates || coordinates.length < 4) {
                     continue;
                  }
                  const coords = (coordinates.slice(0, 4) as [number, number][]);
                  let dataUrl = imgUrl;
                  if (imgUrl.startsWith("http") && !imgUrl.startsWith("data:")) {
                     try {
                        const s3Key = extractS3KeyFromUrl(imgUrl);
                        if (s3Key) {
                           const { blob } = await downloadOverlayImage(s3Key);
                           dataUrl = await blobToDataUrl(blob);
                        } else {
                           const response = await fetch(imgUrl);
                           if (!response.ok) {
                              throw new Error("Unable to fetch overlay image.");
                           }
                           const blob = await response.blob();
                           dataUrl = await blobToDataUrl(blob);
                        }
                     } catch (err) {
                        console.error("Failed to fetch image:", err);
                        continue;
                     }
                  }
                  const layerName = layerSnapshot.datasetName || layerSnapshot.filename || record.documentId || `annotation-${record.id}`;
                  const newLayer = LayerFunc.createLayerInfoForImageOverlay(
                     layerName,
                     layerSnapshot.filename || layerName,
                     dataUrl,
                     coords
                  );
                  newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                  newLayer.filename = layerSnapshot.filename || newLayer.filename;
                  newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                  newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                  mapitStateDispatch({
                     ...actionAddDataLayer(newLayer),
                     callbacks: {
                        onLayerAdded: (li: LayerInfo) => {
                           if (typeof li.layerId === "number") {
                              setTimeout(() => {
                                 editorDispatch({ type: ANNEditorActionType.AddLayer, payload: li.layerId as number });
                              }, 0);
                           }
                        }
                     }
                  } as any);
               } else {
                  layerSnapshot.geoJson.features.forEach(f => {
                     if (f.geometry) {
                        geometriesForZoom.push(f.geometry);
                     }
                  });
                  const label = layerSnapshot.datasetName || layerSnapshot.filename || `${record.documentId || "annotation"}-geometry`;
                  let newLayer: LayerInfo;
                  if (layerSnapshot.geoJson?.type === "FeatureCollection") {
                     newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(layerSnapshot.geoJson, label);
                  } else {
                     newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(
                        { type: "FeatureCollection", features: layerSnapshot.geoJson?.features ?? [] } as any,
                        label
                     );
                  }
                  newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                  newLayer.filename = layerSnapshot.filename || newLayer.filename;
                  newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                  newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                  mapitStateDispatch({
                     ...actionAddDataLayer(newLayer),
                     callbacks: {
                        onLayerAdded: (li: LayerInfo) => {
                           if (typeof li.layerId === "number") {
                              setTimeout(() => {
                                 editorDispatch({ type: ANNEditorActionType.AddLayer, payload: li.layerId as number });
                              }, 0);
                           }
                        }
                     }
                  } as any);
               }
            } else if (layerSnapshot.type === LayerType.ImageOverlay && layerSnapshot.imageOverlay?.coordinates?.length) {
               const coords = layerSnapshot.imageOverlay.coordinates;
               if (coords.length >= 4) {
                  const polygonGeometry: Geometry = {
                     type: "Polygon",
                     coordinates: [coords]
                  };
                  geometriesForZoom.push(polygonGeometry);
               }
            }
         }

         if (geometriesForZoom.length > 0) {
            setTimeout(() => {
               try {
                  const features = geometriesForZoom.map(geom => feature(geom));
                  const fc = featureCollection(features);
                  mapitState.map.zoomToDataAvoidingWindows(fc, 25);
               } catch  {
               }
            }, 600);
         }
         const titleValue = record.title || record.comment;
         if (titleValue) {
            editorDispatch({
               type: ANNEditorActionType.SetStedfaestelseTitle,
               payload: titleValue,
            });
         }
         editorDispatch({
            type: ANNEditorActionType.SetStedfaestelseHistoryComment,
            payload: "",
         });
         if (record.annotationType) {
            editorDispatch({
               type: ANNEditorActionType.SetStedfaestelseAnnotationType,
               payload: record.annotationType,
            });
         }
         if (record.quality) {
            editorDispatch({
               type: ANNEditorActionType.SetStedfaestelseQuality,
               payload: record.quality,
            });
         }
         editorDispatch({
            type: ANNEditorActionType.SetEditSession,
            payload: {
               oldAnnotationId: record.id,
               datoloebenr: record.datoloebenr,
               originalCreatedAt: record.createdAt,
               originalCreatedBy: record.createdBy,
               originalCreatedByName: record.createdByName,
               initialLayerIds: Array.from(initialLayerIds),
            },
         });

         windowDispatch(openWindow(WindowId.ANNEditor, { contextType, identifier }));
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(err?.message || String(err) || Localization.getText("Failed to start editing")));
      } finally {
         setEditingId(null);
      }
   }, [organizationId, contextType, identifier, mapitStateDispatch, editorDispatch, windowDispatch, appMessageDispatch]);

   const handleImport = useCallback(
      async (record: ANNAnnotationRecord) => {
         if (record.type !== AnnotationType.Stedfaestelse) {
            return;
         }
         const layers = record.layers || [];
         const layersToImport = layers.filter(
            (layer) => {
               if (layer.geoJson?.features?.length) {
                  return true;
               }
               if (layer.type === LayerType.ImageOverlay && layer.imageOverlay?.coordinates?.length) {
                  return true;
               }
               return false;
            }
         );
         if (!layersToImport.length) {
            appMessageDispatch(
               actionSetErrorMessage(
                  Localization.getText("No overlays or geometries available")
               )
            );
            return;
         }
         setImportingId(record.id);
         try {
            const geometriesForZoom: Geometry[] = [];

            for (const layerSnapshot of layersToImport) {
               if (layerSnapshot.geoJson?.features?.length) {
                  const isImageOverlay = layerSnapshot.type === LayerType.ImageOverlay;
                  if (isImageOverlay) {
                     const firstFeature = layerSnapshot.geoJson.features[0];
                     const imgUrl = firstFeature?.properties?._imgUrl;
                     if (!imgUrl) {
                        console.warn("ImageOverlay layer missing _imgUrl in properties");
                        continue;
                     }
                     const geometry = firstFeature?.geometry;
                     if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) {
                        console.warn("ImageOverlay layer missing valid polygon geometry");
                        continue;
                     }
                     geometriesForZoom.push(geometry);
                     const coordinates = geometry.type === "Polygon"
                        ? geometry.coordinates[0]
                        : geometry.coordinates[0]?.[0];
                     if (!coordinates || coordinates.length < 4) {
                        continue;
                     }
                     const coords = (coordinates.slice(0, 4) as [number, number][]);
                     let dataUrl = imgUrl;
                     if (imgUrl.startsWith("http") && !imgUrl.startsWith("data:")) {
                        try {
                           const s3Key = extractS3KeyFromUrl(imgUrl);
                           if (s3Key) {
                              const { blob } = await downloadOverlayImage(s3Key);
                              dataUrl = await blobToDataUrl(blob);
                           } else {
                              const response = await fetch(imgUrl);
                              if (!response.ok) {
                                 throw new Error("Unable to fetch overlay image.");
                              }
                              const blob = await response.blob();
                              dataUrl = await blobToDataUrl(blob);
                           }
                        } catch (err) {
                           console.error("Failed to fetch image:", err);
                           continue;
                        }
                     }
                     if (!dataUrl.startsWith("data:")) {
                        console.warn("Image URL was not converted to dataUrl, MapLibre may make duplicate network call:", imgUrl);
                     }
                     const layerName = layerSnapshot.datasetName || layerSnapshot.filename || record.documentId || `annotation-${record.id}`;
                     const newLayer = LayerFunc.createLayerInfoForImageOverlay(
                        layerName,
                        layerSnapshot.filename || layerName,
                        dataUrl,
                        coords
                     );
                     newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                     newLayer.filename = layerSnapshot.filename || newLayer.filename;
                     newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                     newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                     mapitStateDispatch(actionAddDataLayer(newLayer));
                  } else {
                     layerSnapshot.geoJson.features.forEach(f => {
                        if (f.geometry) {
                           geometriesForZoom.push(f.geometry);
                        }
                     });
                     const label = layerSnapshot.datasetName || layerSnapshot.filename || `${record.documentId || "annotation"}-geometry`;
                     let newLayer: LayerInfo;
                     if (layerSnapshot.geoJson?.type === "FeatureCollection") {
                        newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(layerSnapshot.geoJson, label);
                     } else {
                        newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(
                           { type: "FeatureCollection", features: layerSnapshot.geoJson?.features ?? [] } as any,
                           label
                        );
                     }
                     newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                     newLayer.filename = layerSnapshot.filename || newLayer.filename;
                     newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                     newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                     if (layerSnapshot.propertiesInGeoJson) {
                        newLayer.propertiesInGeoJson = [...layerSnapshot.propertiesInGeoJson];
                     }
                     if (layerSnapshot.propertiesToDisplay) {
                        newLayer.propertiesToDisplay = [...layerSnapshot.propertiesToDisplay];
                     }
                     mapitStateDispatch(actionAddDataLayer(newLayer));
                  }
               }
               else if (layerSnapshot.type === LayerType.ImageOverlay && layerSnapshot.imageOverlay?.coordinates?.length) {
                  const polygon: Geometry = {
                     type: "Polygon",
                     coordinates: [layerSnapshot.imageOverlay.coordinates]
                  };
                  geometriesForZoom.push(polygon);
                  const overlay = layerSnapshot.imageOverlay;
                  let dataUrl = overlay.dataUrl;
                  if (!dataUrl || (dataUrl.startsWith("http") && !dataUrl.startsWith("data:"))) {
                     if (overlay.s3Key) {
                        const { blob } = await downloadOverlayImage(overlay.s3Key);
                        dataUrl = await blobToDataUrl(blob);
                     } else if (overlay.s3Url) {
                        const s3Key = extractS3KeyFromUrl(overlay.s3Url);
                        if (s3Key) {
                           const { blob } = await downloadOverlayImage(s3Key);
                           dataUrl = await blobToDataUrl(blob);
                        } else {
                           const response = await fetch(overlay.s3Url);
                           if (!response.ok) {
                              throw new Error("Unable to fetch overlay image.");
                           }
                           const blob = await response.blob();
                           dataUrl = await blobToDataUrl(blob);
                        }
                     } else {
                        continue;
                     }
                  }
                  if (!dataUrl.startsWith("data:")) {
                     console.warn("Image URL was not converted to dataUrl, MapLibre may make duplicate network call:", overlay.s3Url || overlay.dataUrl);
                  }
                  const coords = overlay.coordinates.slice(0, 4) as [number, number][];
                  const layerName = layerSnapshot.datasetName || layerSnapshot.filename || record.documentId || `annotation-${record.id}`;
                  const newLayer = LayerFunc.createLayerInfoForImageOverlay(
                     layerName,
                     layerSnapshot.filename || layerName,
                     dataUrl,
                     coords
                  );
                  newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                  newLayer.filename = layerSnapshot.filename || newLayer.filename;
                  newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                  newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                  mapitStateDispatch(actionAddDataLayer(newLayer));
               }
            }

            if (geometriesForZoom.length > 0 && mapitState.map) {
               setTimeout(() => {
                  const features = geometriesForZoom.map(geom => feature(geom));
                  const fc = featureCollection(features);
                  mapitState.map.zoomToDataAvoidingWindows(fc, 25);
               }, 600);
            }
         } catch (importError: any) {
            appMessageDispatch(actionSetErrorMessage(importError?.message || "Failed to import overlay."));
         } finally {
            setImportingId(null);
         }
      },
      [appMessageDispatch, mapitStateDispatch, mapitState]
   );
   const handleDownloadDocument = useCallback(
      async (record: ANNAnnotationRecord) => {
         if (!record.document?.s3Key) {
            return;
         }
         setDownloadingId(record.id);
         try {
            const { blob, fileName } = await downloadOverlayImage(record.document.s3Key);
            const downloadName = record.document.filename || fileName || `document-${record.id}`;
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = downloadName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
         } catch (err: any) {
            appMessageDispatch(
               actionSetErrorMessage(
                  err?.message || Localization.getText("Failed to download document")
               )
            );
         } finally {
            setDownloadingId(null);
         }
      },
      [appMessageDispatch]
   );
   const handleOpenDocument = useCallback(
      async (record: ANNAnnotationRecord) => {
         if (!record.document?.s3Key) {
            return;
         }
         setDownloadingId(record.id);
         try {
            const { blob } = await downloadOverlayImage(record.document.s3Key);
            const pdfUrl = URL.createObjectURL(blob);
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
               pdfUrl,
               pdfBlob: blob,
               documentId: contextType === AnnotationContextType.Servitut ? (identifier as string | undefined) : undefined,
               bfeNumber: contextType === AnnotationContextType.Ejendom ? identifier : undefined,
               contextType,
               organizationId,
               userId: sessionState?.userRef,
               userName: sessionState?.userName,
               datoloebenr: record.datoloebenr || datoloebenr,
               documentFilename: record.document.filename,
            }));
         } catch (err: any) {
            appMessageDispatch(
               actionSetErrorMessage(
                  err?.message || Localization.getText("Failed to open document")
               )
            );
         } finally {
            setDownloadingId(null);
         }
      },
      [appMessageDispatch, windowDispatch, contextType, identifier, organizationId, sessionState, datoloebenr]
   );
   const handleShowNote = useCallback((record: ANNAnnotationRecord) => {
      const titleLabel = Localization.getText("Title");
      const textLabel = Localization.getText("Note text");
      let htmlParts: string[] = [];
      if (record.title) {
         htmlParts.push(`<strong>${titleLabel}:</strong><br/>${record.title.replace(/\n/g, '<br/>')}`);
      }
      if (record.note?.text) {
         htmlParts.push(`<strong>${textLabel}:</strong><br/>${record.note.text.replace(/\n/g, '<br/>')}`);
      }
      if (htmlParts.length === 0) {
         htmlParts.push(Localization.getText("No note text"));
      }
      const htmlMessage = htmlParts.join("<br/><br/>");
      appMessageDispatch(actionSetInfoMessage(htmlMessage));
   }, [appMessageDispatch]);
   const handleToggleHistory = useCallback((record: ANNAnnotationRecord) => {
      setExpandedHistoryIds(prev => {
         const touchedAt = record.lastEditedAt || record.createdAt;
         const entries = [...(record.history || [])].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
         if (entries.length > 0 && touchedAt && entries[0].timestamp === touchedAt) {
            entries.shift();
         }
         if (entries.length === 0) {
            const next = new Set(prev);
            next.delete(record.id);
            return next;
         }

         const next = new Set(prev);
         if (next.has(record.id)) {
            next.delete(record.id);
         } else {
            next.add(record.id);
         }
         return next;
      });
   }, []);

   const handleOpenScannedAktPdf = useCallback(async (runAIAnalysis = false) => {
      if (contextType !== AnnotationContextType.Servitut) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("PDF viewer only available for servitut")));
         return;
      }

      if (!sessionState?.userRef || !sessionState?.userName || !organizationId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
         return;
      }
      if (runAIAnalysis && !canUseServitutAIAnalyse) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Access denied")));
         return;
      }

      const analyzer = createANNLLMAnalyzer({
         userRef: sessionState.userRef,
         userName: sessionState.userName,
         organizationId: organizationId
      });
      const aktId = analyzer.getAktIdForPdf(servitut, stamOplysninger);

      if (!aktId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF available for this servitut")));
         return;
      }

      setAnalyzing(true);
      setAnalyzingProgress(Localization.getText("Fetching Akt samling..."));

      const timeoutIds: NodeJS.Timeout[] = [];
      let stillWorkingCount = 0;
      const clearAllTimeouts = () => {
         timeoutIds.forEach(id => clearTimeout(id));
         timeoutIds.length = 0;
      };

      try {
         console.log(`[ANNAIAnalyzer] Fetching PDF for aktId: ${aktId}`);
         const pdfBlob = await analyzer.fetchPdf(aktId);
         console.log(`[ANNAIAnalyzer] PDF fetched successfully, size: ${pdfBlob.size} bytes`);

         const metadata = analyzer.buildMetadata(servitut || {}, stamOplysninger, xmlDoc, viamapTingBog, propertyData);
         console.log(`[ANNAIAnalyzer] Metadata built:`, metadata);

         let analysisResult: LLMAnalysisResult | undefined;
         if (runAIAnalysis) {
            console.log(`[ANNAIAnalyzer] Calling AI service...`);
            setAnalyzingProgress(Localization.getText("Analyzing with AI..."));

            timeoutIds.push(setTimeout(() => {
               setAnalyzingProgress(Localization.getText("AI analysis might take some time..."));
            }, 10000));
            timeoutIds.push(setTimeout(() => {
               stillWorkingCount = 1;
               setAnalyzingProgress(Localization.getText("Still working..."));
               const updateStillWorking = () => {
                  stillWorkingCount++;
                  const dots = ".".repeat(Math.min(stillWorkingCount, 100));
                  setAnalyzingProgress(`${Localization.getText("Still working")}${dots}`);

                  if (stillWorkingCount < 100) {
                     timeoutIds.push(setTimeout(updateStillWorking, 2000));
                  }
               };

               timeoutIds.push(setTimeout(updateStillWorking, 2000));
            }, 20000));

            try {
               analysisResult = await analyzer.analyzePdf(pdfBlob, metadata);
               console.log(`[ANNAIAnalyzer] AI analysis result:`, analysisResult);

               clearAllTimeouts();
               setAnalyzingProgress("");
            } catch (llmError: any) {
               console.error(`[ANNAIAnalyzer] AI service failed:`, llmError);
               clearAllTimeouts();
               setAnalyzingProgress("");
               appMessageDispatch(actionSetInfoMessage(
                  Localization.getText("AI analysis failed, showing PDF without highlights")
               ));
            }
         }

         const pdfUrl = URL.createObjectURL(pdfBlob);
         windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
            pdfUrl,
            pdfBlob,
            analysisResult,
            aktId,
            documentId: identifier as string,
            contextType: contextType,
            organizationId: organizationId,
            userId: sessionState?.userRef,
            userName: sessionState?.userName,
            datoloebenr: datoloebenr,
            documentFilename: metadata.document
         }));

      } catch (err: any) {
         clearAllTimeouts();
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText(runAIAnalysis ? "Failed to analyze PDF" : "Failed to open scanned akt PDF")
         ));
      } finally {
         clearAllTimeouts();
         setAnalyzing(false);
         setAnalyzingProgress("");
      }
   }, [contextType, servitut, stamOplysninger, xmlDoc, viamapTingBog, propertyData, appMessageDispatch, windowDispatch, sessionState, organizationId, identifier, datoloebenr, canUseServitutAIAnalyse]);

   const renderQualityCell = useCallback((quality?: AnnotationQuality, verified?: boolean, verifierInfo?: string) => {
      if (!quality) {
         return <div className="ann-components-quality-cell-empty">-</div>;
      }
      const qualityLabel = quality === AnnotationQuality.Gold
         ? Localization.getText("Gold")
         : quality === AnnotationQuality.Silver
            ? Localization.getText("Silver") || "Silver"
            : Localization.getText("Bronze") || "Bronze";
      const statusText = verified
         ? (verifierInfo || (Localization.getText("Verified") || "Verified"))
         : (Localization.getText("Not verified") || "Not verified");
      const statusTooltip = (
         <div className="ann-tooltip">
            <div className="ann-tooltip__title">{qualityLabel}</div>
            <div className="ann-tooltip__body">{statusText}</div>
         </div>
      );
      return (
         <div
            className="ann-components-quality-cell"
         >
            <div className={`ann-quality-badge ann-quality-${quality.toLowerCase()}`}>
               <span className="ann-quality-badge__dot" aria-hidden="true" />
               <TooltipWrapper title={statusTooltip}>
                  <span className={`ann-quality-badge__status ${verified ? "ann-quality-badge__status--verified" : "ann-quality-badge__status--unverified"}`}>
                     {verified ? "✓" : "–"}
                  </span>
               </TooltipWrapper>
            </div>
         </div>
      );
   }, []);

   const renderEllipsisCell = useCallback((valueRaw: unknown) => {
      const value = valueRaw === undefined || valueRaw === null ? "-" : String(valueRaw);
      if (!value || value === "-") {
         return <span className="ann-cell-ellipsis">-</span>;
      }
      return (
         <TooltipWrapper title={value}>
            <span className="ann-cell-ellipsis">{value}</span>
         </TooltipWrapper>
      );
   }, []);

   const columns = [
      {
         key: 'primaryAction',
         name: '',
         width: 44,
         minWidth: 44,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return null;
            return (
               <ANNAnnotationPrimaryActionCell
                  record={props.row?._record}
                  importingId={importingId}
                  downloadingId={downloadingId}
                  onShowNote={handleShowNote}
                  onImport={handleImport}
                  onOpenDocument={handleOpenDocument}
                  onDownloadDocument={handleDownloadDocument}
               />
            );
         }
      },
      {
         key: 'title',
         name: Localization.getText('Title') || 'Title',
         width: 220,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               return (
                  <div className="ann-history-label">
                     <span className="ann-history-label__arrow">└─</span>
                     <span className="ann-history-label__text">{Localization.getText("History") || "History"}</span>
                  </div>
               );
            }
            return renderEllipsisCell(props.row?.title || "-");
         }
      },
      {
         key: 'annotationType',
         name: Localization.getText('Annotation Type'),
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return <span>-</span>;
            return renderEllipsisCell(props.row?.annotationType || "-");
         }
      },
      ...(contextType === AnnotationContextType.Servitut ? [{
         key: 'datoloebenr',
         name: Localization.getText('Date/serial number'),
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return <span>-</span>;
            return renderEllipsisCell(props.row?.datoloebenr || "-");
         }
      }] : []),
      ...(contextType === AnnotationContextType.Ejendom ? [{
         key: 'bfeNumber',
         name: Localization.getText('BFE Number'),
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return <span>-</span>;
            return renderEllipsisCell(props.row?.bfeNumber || "-");
         }
      }] : []),
      {
         key: 'quality',
         name: Localization.getText('Status') || 'Status',
         width: 72,
         renderCell: (props: any) => {
            const annotation = props.row?._record as ANNAnnotationRecord | undefined;
            if (!annotation) return <span>-</span>;
            if (props.row?._isHistoryRow) {
               const entry = props.row?._historyEntry;
               if (!entry?.quality && !entry?.verified) return <span>-</span>;
               return renderQualityCell(entry?.quality, entry?.verified);
            }
            const displayQuality = annotation.quality;
            const isVerified = !!annotation.verified;
            const verifierInfo = isVerified && annotation.lastEditedByName
               ? `${Localization.getText("Verified by")}: ${annotation.lastEditedByName}${annotation.lastEditedAt ? `, ${new Date(annotation.lastEditedAt).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" })}` : ""}`
               : "";
            return renderQualityCell(displayQuality, isVerified, verifierInfo);
         }
      },
      {
         key: 'createdAt',
         name: Localization.getText('Last Edited') || 'Last edited',
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               return renderEllipsisCell(props.row?.createdAt || "-");
            }
            return renderEllipsisCell(props.row?.createdAt || "-");
         }
      },
      {
         key: 'createdByName',
         name: Localization.getText('Last Edited by') || 'Last edited by',
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               return renderEllipsisCell(props.row?.createdByName || "-");
            }
            return renderEllipsisCell(props.row?.createdByName || "-");
         }
      },
      {
         key: 'historyComment',
         name: Localization.getText('Comment') || 'Comment',
         width: 260,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               const entry = props.row?._historyEntry;
               return renderEllipsisCell(entry?.comment || "-");
            }
            return renderEllipsisCell(props.row?.historyComment || "-");
         }
      },
      {
         key: 'actions',
         name: Localization.getText('Actions'),
         width: 160,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return null;
            const record = props.row?._record as ANNAnnotationRecord | undefined;
            const historyAvailable = record ? getMeaningfulHistoryEntries(record).length > 0 : false;
            return (
               <ANNAnnotationActionsCell
                  record={props.row?._record}
                  importingId={importingId}
                  downloadingId={downloadingId}
                  deletingId={deletingId}
                  editingId={editingId}
                  canVerify={canVerifyQuality}
                  canDelete={canDelete}
                  canWrite={canWrite}
                  contextType={contextType}
                  onImport={handleImport}
                  onDownloadDocument={handleDownloadDocument}
                  onShowNote={handleShowNote}
                  onDelete={handleDelete}
                  onEditNote={handleEditNote}
                  onEditStedfaestelse={handleEditStedfaestelse}
                  onEditDocument={handleEditDocument}
                  onToggleHistory={handleToggleHistory}
                  historyAvailable={historyAvailable}
                  isHistoryOpen={historyAvailable && !!props.row?._record && expandedHistoryIds.has(props.row._record.id)}
                  hidePrimary={true}
               />
            );
         }
      }
   ];
   const baseRows = annotations
      .slice()
      .sort((a, b) => {
         const aTouchedAt = a.lastEditedAt || a.createdAt;
         const bTouchedAt = b.lastEditedAt || b.createdAt;
         return new Date(aTouchedAt).getTime() - new Date(bTouchedAt).getTime();
      })
      .map((annotation) => {
         const overlayCount =
            (annotation.layers || []).filter(
               (layer) =>
                  layer.type === LayerType.ImageOverlay &&
                  (layer.geoJson?.features?.length || layer.imageOverlay?.coordinates?.length)
            ).length || 0;
         const geoLayerCount =
            (annotation.layers || []).filter(
               (layer) =>
                  (layer.type === LayerType.GeoJSON_Line || layer.type === LayerType.GeoJSON_Polygon || layer.type === LayerType.GeoJSON_Point) &&
                  layer.geoJson?.features?.length
            ).length || 0;
         const technicalType = annotation.type === AnnotationType.Note
            ? Localization.getText("Note")
            : annotation.type === AnnotationType.Document
               ? Localization.getText("Document")
               : Localization.getText("Stedfaestelse");
         const touchedAt = annotation.lastEditedAt || annotation.createdAt;
         const touchedByName =
            annotation.lastEditedByName ||
            annotation.createdByName ||
            annotation.lastEditedBy ||
            annotation.createdBy;
         const currentHistoryComment = getCurrentHistoryComment(annotation);
         return {
            _record: annotation,
            type: technicalType,
            annotationType: annotation.annotationType || "-",
            datoloebenr: annotation.datoloebenr || "-",
            bfeNumber: annotation.bfeNumber || "-",
            quality: annotation.verifiedQuality || annotation.quality || null,
            createdAt: new Date(touchedAt).toLocaleString(Localization.getLocale()),
            _createdAtSort: new Date(touchedAt).getTime(),
            createdByName: touchedByName,
            title: annotation.title || "-",
            historyComment: currentHistoryComment,
            overlayCount,
            geoLayerCount,
         };
      });
   type SortColumn = { columnKey: string; direction: 'ASC' | 'DESC' };
   const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
   const sortedRows = useMemo(() => {
      if (!sortColumns || sortColumns.length === 0) return baseRows;
      const s = [...baseRows];
      s.sort((a: any, b: any) => {
         for (const sort of sortColumns) {
            const key = sort.columnKey;
            let av: any = a[key];
            let bv: any = b[key];
            if (key === 'createdAt') {
               av = a._createdAtSort ?? av;
               bv = b._createdAtSort ?? bv;
            }
            if (av == null && bv == null) continue;
            if (av == null) return sort.direction === 'ASC' ? -1 : 1;
            if (bv == null) return sort.direction === 'ASC' ? 1 : -1;
            if (typeof av === 'string' && typeof bv === 'string') {
               const cmp = av.localeCompare(bv);
               if (cmp !== 0) return sort.direction === 'ASC' ? cmp : -cmp;
            } else {
               if (av > bv) return sort.direction === 'ASC' ? 1 : -1;
               if (av < bv) return sort.direction === 'ASC' ? -1 : 1;
            }
         }
         return 0;
      });
      return s;
   }, [baseRows, sortColumns]);
   const displayRows = useMemo(() => {
      const rows: any[] = [];
      sortedRows.forEach(row => {
         rows.push(row);
         if (expandedHistoryIds.has(row._record.id)) {
            const meaningfulHistoryEntries = getMeaningfulHistoryEntries(row._record);
            meaningfulHistoryEntries.forEach((entry) => {
               rows.push({
                  _isHistoryRow: true,
                  _record: row._record,
                  _historyEntry: entry,
                  type: "",
                  annotationType: "",
                  datoloebenr: "",
                  bfeNumber: "",
                  quality: "",
                  createdAt: new Date(entry.timestamp).toLocaleString(Localization.getLocale()),
                  _createdAtSort: row._createdAtSort,
                  createdByName: entry.userName || entry.userId,
                  title: "",
               });
            });
         }
      });
      return rows;
   }, [sortedRows, expandedHistoryIds, getMeaningfulHistoryEntries]);

   const showTable = displayRows.length > 0;
   return (
      <GlassFoldUdBox title={Localization.getText("Annotations")} group={group} foldetUd={true}>
         <div className="ann-components-grid-container ann-grid-container">
            <div className="ann-components-button-group">
               {canWrite && (
                  <>
                     <GlassButton disabled={identifier === undefined || identifier === null || identifier === ""} onClick={() => {
                        setCreateType(AnnotationType.Note);
                        setShowCreateModal(true);
                     }}>
                        {Localization.getText("Create note")}
                     </GlassButton>
                     <GlassButton disabled={identifier === undefined || identifier === null || identifier === ""} onClick={() => {
                        setCreateType(AnnotationType.Document);
                        setShowCreateModal(true);
                     }}>
                        {Localization.getText("Create document")}
                     </GlassButton>
                     <GlassButton
                        disabled={identifier === undefined || identifier === null || identifier === ""}
                        onClick={handleOpenEditor}
                     >
                        {Localization.getText("Stedfaestelse")}
                     </GlassButton>
                     {contextType === AnnotationContextType.Servitut && !isServitutDigital(datoloebenr) && (
                        <GlassButton
                           disabled={analyzing || !servitut || (!servitut.dokumentFilnavn && !stamOplysninger?.indskannetAktDokumentId)}
                           onClick={() => handleOpenScannedAktPdf(false)}
                        >
                           {analyzing && (
                              <span className="ann-components-analyzeSpinnerWrap">
                                 <Spinner animation="border" size="sm" className="ann-components-analyzeSpinner" />
                              </span>
                           )}
                           {analyzing
                              ? (analyzingProgress || Localization.getText("Opening scanned akt PDF..."))
                              : Localization.getText("Open scanned akt PDF")}
                        </GlassButton>
                     )}
                  </>
               )}
               <ViewButtonIcon
                  title={Localization.getText("Refresh annotations")}
                  onClick={() => loadAnnotations()}
                  disabled={loading || identifier === undefined || identifier === null || identifier === ""}
               >
                  {loading ? (
                     <Spinner animation="border" size="sm" className="ann-components-refreshSpinner" />
                  ) : (
                        <MIBIcon icon="refresh" />
                  )}
               </ViewButtonIcon>
            </div>
            {(identifier === undefined || identifier === null || identifier === "") ? (
               <div className="ann-components-error">
                  {contextType === AnnotationContextType.Servitut
                     ? Localization.getText("No document selected")
                     : Localization.getText("No BFE number available")}
               </div>
            ) : null}
            {error && annotations.length === 0 ? (
               <div className="ann-components-error">{error}</div>
            ) : null}
            {showTable ? (
               <ReactDataGrid
                  className='rdg-light ann-table-no-scroll'
                  columns={columns}
                  rows={displayRows}
                  sortColumns={sortColumns}
                  onSortColumnsChange={setSortColumns as any}
                  defaultColumnOptions={{ resizable: true, sortable: true }}
                  rowHeight={35}
                  rowClass={(row: any) => row?._isHistoryRow ? "ann-history-row" : ""}
                  enableVirtualization={false}
               />
            ) : null}
            {showCreateModal && (identifier !== undefined && identifier !== null && identifier !== "") && createType === AnnotationType.Stedfaestelse && (
               <ANNCreateStedfaestelse
                  open
                  contextType={contextType}
                  documentId={contextType === AnnotationContextType.Servitut ? identifier as string : undefined}
                  bfeNumber={contextType === AnnotationContextType.Ejendom ? identifier : undefined}
                  datoloebenr={contextType === AnnotationContextType.Servitut ? datoloebenr : undefined}
                  onClose={() => {
                     setShowCreateModal(false);
                     setCreateType(null);
                  }}
                  onCreated={async () => {
                     setShowCreateModal(false);
                     setCreateType(null);
                     await new Promise(resolve => setTimeout(resolve, 1000));
                     await loadAnnotations();
                  }}
               />
            )}
            {showCreateModal && (identifier !== undefined && identifier !== null && identifier !== "") && createType === AnnotationType.Note && (
               <ANNCreateNote
                  open
                  contextType={contextType}
                  documentId={contextType === AnnotationContextType.Servitut ? identifier as string : undefined}
                  bfeNumber={contextType === AnnotationContextType.Ejendom ? identifier : undefined}
                  datoloebenr={contextType === AnnotationContextType.Servitut ? datoloebenr : undefined}
                  onClose={() => {
                     setShowCreateModal(false);
                     setCreateType(null);
                  }}
                  onCreated={async () => {
                     setShowCreateModal(false);
                     setCreateType(null);
                     await new Promise(resolve => setTimeout(resolve, 1000));
                     await loadAnnotations();
                  }}
               />
            )}
            {showCreateModal && (identifier !== undefined && identifier !== null && identifier !== "") && createType === AnnotationType.Document && (
               <ANNCreateDocument
                  open
                  contextType={contextType}
                  documentId={contextType === AnnotationContextType.Servitut ? identifier as string : undefined}
                  bfeNumber={contextType === AnnotationContextType.Ejendom ? identifier : undefined}
                  datoloebenr={contextType === AnnotationContextType.Servitut ? datoloebenr : undefined}
                  onClose={() => {
                     setShowCreateModal(false);
                     setCreateType(null);
                  }}
                  onCreated={async () => {
                     setShowCreateModal(false);
                     setCreateType(null);
                     await new Promise(resolve => setTimeout(resolve, 1000));
                     await loadAnnotations();
                  }}
               />
            )}
            {editNoteModalOpen && annotationToEdit && organizationId && (
               <ANNCreateNote
                  open={editNoteModalOpen}
                  contextType={contextType}
                  documentId={contextType === AnnotationContextType.Servitut ? annotationToEdit.documentId : undefined}
                  bfeNumber={contextType === AnnotationContextType.Ejendom ? annotationToEdit.bfeNumber : undefined}
                  datoloebenr={contextType === AnnotationContextType.Servitut ? annotationToEdit.datoloebenr : undefined}
                  editAnnotation={annotationToEdit}
                  oldAnnotationId={annotationToEdit.id}
                  onClose={() => {
                     setEditNoteModalOpen(false);
                     setAnnotationToEdit(null);
                  }}
                  onCreated={async () => {
                     setEditNoteModalOpen(false);
                     setAnnotationToEdit(null);
                     await new Promise(resolve => setTimeout(resolve, 1000));
                     await loadAnnotations();
                  }}
               />
            )}
            {editDocumentModalOpen && documentToEdit && organizationId && (
               <ANNCreateDocument
                  open={editDocumentModalOpen}
                  contextType={contextType}
                  documentId={contextType === AnnotationContextType.Servitut ? documentToEdit.documentId : undefined}
                  bfeNumber={contextType === AnnotationContextType.Ejendom ? documentToEdit.bfeNumber : undefined}
                  datoloebenr={contextType === AnnotationContextType.Servitut ? documentToEdit.datoloebenr : undefined}
                  editAnnotation={documentToEdit}
                  oldAnnotationId={documentToEdit.id}
                  onClose={() => {
                     setEditDocumentModalOpen(false);
                     setDocumentToEdit(null);
                  }}
                  onCreated={async () => {
                     setEditDocumentModalOpen(false);
                     setDocumentToEdit(null);
                     await new Promise(resolve => setTimeout(resolve, 1000));
                     await loadAnnotations();
                  }}
               />
            )}
         </div>
      </GlassFoldUdBox>
   );
}
